import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/synapse/Modal';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { translateUnit } from '@/lib/itemTypeConfig';
import type { StockMovementLogRow } from '@/types/api';

interface DeleteMovementDialogProps {
  row: StockMovementLogRow;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error: unknown;
}

/**
 * Confirm removing a ledger line.
 *
 * States the consequence rather than asking abstractly. Deleting a stock-in
 * takes its batch with it — that batch has no other movements, by the same rule
 * that made the row deletable — and a user who thought they were fixing a
 * number should not discover the batch is gone afterwards.
 */
export function DeleteMovementDialog({
  row,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteMovementDialogProps) {
  const { t } = useTranslation();
  const isStockIn = row.kind === 'STOCK_IN';
  const unit = translateUnit(t, row.itemUnit);

  return (
    <Modal
      title={t('activity.deleteTitle')}
      description={t('activity.deleteBody')}
      onClose={onClose}
      isPending={isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending} full>
            {t('activity.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={isPending} full>
            {isPending ? t('activity.deleting') : t('activity.confirmDelete')}
          </Button>
        </>
      }
    >
      <div className="rounded-md bg-paper-2 px-3 py-2.5">
        <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{row.itemBrandName}</div>
        <div className="mt-0.5 text-[12px] text-muted">
          {row.quantity > 0 ? `+${row.quantity}` : row.quantity} {unit}
          {row.batchNumber ? ` · ${row.batchNumber}` : ` · ${t('activity.noBatch')}`}
        </div>
      </div>

      {isStockIn && (
        <p className="text-[12.5px] text-muted">{t('activity.deleteBatchWarning')}</p>
      )}

      {error != null && (
        <p
          role="alert"
          className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive"
        >
          {error instanceof ApiError ? translateApiError(error) : t('errors.fallback')}
        </p>
      )}
    </Modal>
  );
}
